import {
  IonContent,
  IonPage,
  IonHeader,
  IonModal,
  IonToolbar,
  IonTitle,
  useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { arrowBack, call, menuOutline, optionsOutline } from "ionicons/icons";
import { ElementType, lazy, ReactElement, Suspense, useState } from "react";
import Icon from "../../atoms/Icon";
import Menu from "../../molecules/Menu/Menu";
import { useResonanceLayout } from "./ResonanceLayoutContext";
import type { HeaderActionId } from "../../../projects/types";
import { MINI_LOGO, LOGO } from "../../../constants/images";
import { PAGE_BACKGROUND } from "../../../theme/pageStyle";
import { useHistory } from "react-router";
import { useStudySlug } from "../../../hooks/useStudySlug";
import styled from "styled-components";
import { getProjectConfig } from "../../../projects";
import { useAuth } from "../../../contexts/AuthContext";

// Island settings belong to the lazy island-map experience — never load its
// chunk from the shared layout for studies without it.
const IslandSettings = lazy(() =>
  import("../../../pages/MentalPlusPages").then((m) => ({
    default: m.IslandSettings,
  }))
);

interface ResonanceLayoutProps {
  children?: ReactElement;
  isConnected?: boolean;
  /**
   * Host the layout OUTSIDE the router's page stack (content preview frame): renders a
   * plain div with the ion-page CLASS — identical Ionic page CSS (absolute fill, flex
   * column) — instead of <IonPage>, whose PageManager would register this nested page
   * into the ROUTE's view item (hijacking the outer page's element) and stamp it
   * ion-page-invisible during route transitions (stuck-white-page bug).
   */
  standalone?: boolean;
}

/** Rendered width of each header action, its own outer margin included —
 *  what the title has to leave free on that side (see titleInset). */
const HEADER_ACTION_WIDTH: Record<HeaderActionId, number> = {
  logo: 56, // 12px inset + 44px disc
  back: 80,
  menu: 42,
  account: 42,
  islandSettings: 42,
  call: 42,
};
const HEADER_SLOT_GAP = 10;
const LEFT_SLOT_INSET = 0;
const RIGHT_SLOT_INSET = 10;

/** The decoration that comes with the header edge (CSS in theme/common.scss
 *  shows the one matching html[data-header-shape]; flat shows none). */
const PageDecoration = (): ReactElement => (
  <div className="page-decoration" aria-hidden="true">
    <span className="page-decoration__set page-decoration__set--bubbles">
      <svg className="page-decoration__a" width="70" height="150" viewBox="0 0 70 150">
        <circle cx="20" cy="22" r="10" fill="none" stroke="#ffffff" strokeWidth="2.5" />
        <circle cx="48" cy="70" r="6" fill="none" stroke="#ffffff" strokeWidth="2" />
        <circle cx="26" cy="118" r="14" fill="none" stroke="#ffffff" strokeWidth="2.5" />
      </svg>
      <svg className="page-decoration__b" width="60" height="90" viewBox="0 0 60 90">
        <circle cx="18" cy="20" r="8" fill="none" stroke="#ffffff" strokeWidth="2" />
        <circle cx="40" cy="64" r="12" fill="none" stroke="#ffffff" strokeWidth="2.5" />
      </svg>
    </span>
    <span className="page-decoration__set page-decoration__set--clouds">
      <svg className="page-decoration__a" width="150" height="62" viewBox="0 0 150 62">
        <circle cx="40" cy="38" r="22" fill="#ffffff" />
        <circle cx="74" cy="28" r="26" fill="#ffffff" />
        <circle cx="108" cy="38" r="20" fill="#ffffff" />
        <rect x="18" y="38" width="112" height="22" rx="11" fill="#ffffff" />
      </svg>
      <svg className="page-decoration__b" width="120" height="50" viewBox="0 0 150 62">
        <circle cx="40" cy="38" r="22" fill="#ffffff" />
        <circle cx="74" cy="28" r="26" fill="#ffffff" />
        <circle cx="108" cy="38" r="20" fill="#ffffff" />
        <rect x="18" y="38" width="112" height="22" rx="11" fill="#ffffff" />
      </svg>
    </span>
    <span className="page-decoration__set page-decoration__set--circles">
      <i className="page-decoration__a" />
      <i className="page-decoration__b" />
    </span>
  </div>
);

const ResonanceLayout = ({
  children,
  isConnected,
  standalone,
}: ResonanceLayoutProps): ReactElement => {
  const {
    disableNav,
    disableHeader,
    isMenuVisible,
    setResonanceLayout,
    backgroundColor,
    title,
    welcomePage,
    headerBackground,
    headerLeft,
    headerRight,
    headerBackFallbackPath,
  } = useResonanceLayout();
  const [isIslandSettingsOpen, setIsIslandSettingsOpen] = useState(false);

  let history = useHistory();
  const ionRouter = useIonRouter();
  const study_slug = useStudySlug();
  const { isAuthenticated } = useAuth();
  // Per-project brand logo (white-label); Mental+ has none set -> art fallback.
  const project = getProjectConfig(study_slug);
  const { t } = useTranslation();

  /**
   * Header slot resolution: page override (context) > project config > the
   * historical default (logo left, burger right). Resolved once here so pages
   * only ever state their exception and projects only their layout; neither
   * needs to know about the other. Actions with a missing prerequisite
   * (call without a number) drop out in renderHeaderAction, so a page may list
   * an action that only some projects support.
   */
  const leftActions = headerLeft ?? project.header?.left ?? ["logo"];
  const rightActions = headerRight ?? project.header?.right ?? ["menu"];

  // The title is centred between two side slots of the SAME width (the wider of
  // the two), so it stays centred and can never run into a button; a title too
  // long for the space left ends with an ellipsis.
  const isActionShown = (id: HeaderActionId): boolean =>
    id === "logo" ? !welcomePage : id === "call" ? Boolean(project.header?.callNumber) : true;
  const slotWidth = (ids: HeaderActionId[], inset: number): number => {
    const shown = ids.filter(isActionShown);
    if (!shown.length) return 0;
    return (
      inset +
      shown.reduce((sum, id) => sum + HEADER_ACTION_WIDTH[id], 0) +
      HEADER_SLOT_GAP * (shown.length - 1)
    );
  };
  const titleInset =
    Math.max(
      slotWidth(leftActions, LEFT_SLOT_INSET),
      slotWidth(rightActions, RIGHT_SLOT_INSET),
      16
    ) + 8;
  // Long titles («Compte et paramètres») step down a size before the ellipsis
  // has to cut them.
  const titleText = t(title);
  const titleSize: HeaderTitleSize =
    titleText.length > 22 ? "small" : titleText.length > 16 ? "medium" : "regular";

  // The tinted page brings the decoration that matches the header edge
  // (bubbles / clouds / pale circles / none) — CSS picks it from the theme's
  // data-header-shape. Question screens, island seas and other custom page
  // backgrounds never get it.
  const showDecoration = backgroundColor === PAGE_BACKGROUND && !disableHeader && !welcomePage;

  // The researcher-uploaded LOGO study_config (useConfig().app.logo) is
  // deliberately NOT used for the chrome logos: a preference for it here
  // replaced Mental+'s full wordmark with the uploaded mini icon in
  // production (2026-07-03). The header/welcome logos are project ART
  // (constants/images), like before the white-label refactor.
  const setIsMenuVisible = (value: boolean) => {
    setResonanceLayout({
      isMenuVisible: value,
    });
  };

  // useEffect(() => {
  //   const { app } = useConfig();
  //   updateManifest(app?.name, app?.logo);
  // }, [app]);

  const handleLogoClick = () => {
    history.push(`/${study_slug}/home`); // Navigate to home page
  };

  const handleBackClick = () => {
    if (ionRouter.canGoBack()) {
      ionRouter.goBack();
      return;
    }

    if (headerBackFallbackPath) {
      history.push(headerBackFallbackPath);
    }
  };

  /**
   * One renderer per named action. Returning null (missing prerequisite,
   * welcome page logo) simply removes the action from its slot — the flex slot
   * closes the gap, so neighbours are unaffected.
   */
  const renderHeaderAction = (id: HeaderActionId): ReactElement | null => {
    switch (id) {
      case "logo":
        // The welcome page shows the centered BigLogo instead — the corner
        // crop would duplicate it.
        if (welcomePage) return null;
        return (
          // On a white disc, so the logo's own colors read on any header color.
          <LogoDisc key="logo">
            <Logo
              // Header corner slot always shows the minimal crop; the full
              // wordmark belongs to the welcome page's centered BigLogo (a
              // "full" corner option was tried 2026-07-03 and reverted — the
              // wordmark doesn't read at this size).
              src={project.miniLogo ?? project.logo ?? MINI_LOGO}
              alt=""
            />
          </LogoDisc>
        );

      case "back":
        return (
          <HeaderBackButton
            key="back"
            type="button"
            onClick={handleBackClick}
            aria-label={t("common_previous")}
          >
            <Icon icon={arrowBack} />
          </HeaderBackButton>
        );

      case "menu":
        return (
          <HeaderIconButton
            key="menu"
            type="button"
            onClick={() => setIsMenuVisible(true)}
            aria-label={t("header_menu_aria")}
          >
            <Icon icon={menuOutline} size="large" />
          </HeaderIconButton>
        );

      case "account":
        // Same glyph as "menu" on purpose: for the participant this IS the menu
        // button, it just opens the Compte page directly rather than a drawer of
        // links into it. Pushed (not replaced) so the page gets a back arrow home
        // — see DisplayAccount, which derives that from the project's tab list.
        //
        // Signed out, it falls back to the DRAWER: /tabs/account is a private
        // route, so sending a visitor there would only bounce them back, and the
        // welcome/signup screens would lose the one thing the burger gives them —
        // the language selector. Read from AuthContext rather than the
        // `isConnected` prop, which pages pass inconsistently (Resources, Suivi
        // and Calendar omit it while being just as authenticated).
        return (
          <HeaderIconButton
            key="account"
            type="button"
            onClick={() =>
              isAuthenticated
                ? ionRouter.push(`/${study_slug}/tabs/account`)
                : setIsMenuVisible(true)
            }
            aria-label={t("header_menu_aria")}
          >
            <Icon icon={menuOutline} size="large" />
          </HeaderIconButton>
        );

      case "islandSettings":
        return (
          <HeaderIconButton
            key="islandSettings"
            type="button"
            onClick={() => setIsIslandSettingsOpen(true)}
            aria-label={t("header_island_settings_aria")}
          >
            <Icon icon={optionsOutline} size="large" />
          </HeaderIconButton>
        );

      case "call": {
        // Prerequisite: a configured number. Silently absent otherwise, so
        // island-map pages can list "call" without knowing the project.
        const number = project.header?.callNumber;
        if (!number) return null;
        return (
          <HeaderCallButton
            key="call"
            href={`tel:${number}`}
            aria-label={t("header_call_aria", { number })}
          >
            <Icon icon={call} />
          </HeaderCallButton>
        );
      }

      default:
        return null;
    }
  };

// Standalone hosting (preview frame) swaps IonPage for a plain div carrying the same
// ion-page class — see the `standalone` prop doc above.
const Page: ElementType = standalone ? "div" : IonPage;

return (
   // The page background is painted on BOTH IonPage and IonContent: IonContent is
   // width-capped (maxWidth 1200px), so on wide screens IonPage fills the gutters —
   // a per-content page_style background must reach edge to edge.
   <Page className={standalone ? "ion-page" : undefined} style={{ background: backgroundColor }}>
  {!disableHeader && welcomePage !== undefined && (
    <IonHeader className="ion-no-border">
      <WaveToolbar $headerBg={headerBackground}
      >
        <IonTitle>
          {!welcomePage && (
            <HeaderTitle $size={titleSize} style={{ left: titleInset, right: titleInset }}>
              {titleText}
            </HeaderTitle>
          )}
        </IonTitle>

        <LeftHeaderSlot>{leftActions.map(renderHeaderAction)}</LeftHeaderSlot>
        <RightHeaderSlot>{rightActions.map(renderHeaderAction)}</RightHeaderSlot>
      </WaveToolbar>
    </IonHeader>
  )}

  {showDecoration && <PageDecoration />}

  {welcomePage && <BigLogoWrapper><BigLogo src={project.logo ?? LOGO} /></BigLogoWrapper>}

  <IonContent className={!disableNav ? "with-tabbar" : "no-tabbar"}
    style={{
      position: "relative",
      // No backgroundColor here: it is painted ONCE on IonPage (above). IonContent's
      // --background is transparent, so the page color shows through the capped column
      // AND fills the >1200px gutters — painting both layers made translucent page
      // styles (primary-soft) composite twice and show a seam at the cap.
      display: "flex",
      flexDirection: "column",
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
      alignSelf: "center",
    }}
    fullscreen={true}
  >
    {children}
  </IonContent>

  {isMenuVisible && (
    <Menu
      onClose={() => setIsMenuVisible(false)}
      isConnected={isConnected}
    />
  )}

  {getProjectConfig(study_slug).homeExperience === "island-map" && (
    <IonModal
      className="resonance-sheetmodal"
      isOpen={isIslandSettingsOpen}
      backdropDismiss={true}
      breakpoints={[0, 1]}
      initialBreakpoint={1}
      canDismiss={true}
      onDidDismiss={() => setIsIslandSettingsOpen(false)}
    >
      <IonToolbar>
        <IonTitle>Island Setting</IonTitle>
        <CloseButton slot="end" onClick={() => setIsIslandSettingsOpen(false)}>
          x
        </CloseButton>
      </IonToolbar>

      <Suspense fallback={null}>
        <IslandSettings />
      </Suspense>
    </IonModal>
  )}
</Page>

  );
};

/**
 * Header slots — the two flex anchors the named actions render into, in array
 * order (left-to-right). NOTE the actual containing block: slotted absolute
 * children anchor to Ionic's shadow .toolbar-container (position: relative),
 * NOT the WaveToolbar host — the geometry spans the full wave band only
 * because WaveToolbar forces display: flex on the host, stretching that
 * container to the band height. Same containment the old single-purpose
 * Logo/MenuButton relied on; if WaveToolbar's display: flex is ever removed,
 * these slots collapse to Ionic's --min-height strip at the top of the band.
 */
/* Centred on the same line as the title (--app-header-center, set on
   WaveToolbar): a box twice that tall, anchored at the top. */
const HeaderSlot = styled.div`
  position: absolute;
  top: 0;
  height: calc(var(--app-header-center, 40%) * 2);
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
`;

/* left: 0 (not 15px) so the back pill's flush "drawer" design still bleeds off
   the screen edge; inset for other actions comes from their own padding. */
const LeftHeaderSlot = styled(HeaderSlot)`
  left: 0;
`;

const RightHeaderSlot = styled(HeaderSlot)`
  right: 10px;
`;

/* White disc behind the logo: the logo's own colors read on any header color.
   The shadow is dropped on dark headers (tokens.ts --app-header-button-shadow). */
const LogoDisc = styled.span`
  flex: none;
  width: 44px;
  height: 44px;
  margin-left: 12px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: var(--app-header-button-shadow, 0 6px 14px -8px rgba(0, 0, 0, 0.35));
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Logo = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
`;

type HeaderTitleSize = "regular" | "medium" | "small";
/** Phone sizes: regular = Ionic's h1 (1.625rem = 26px), as before. */
const HEADER_TITLE_SIZE: Record<HeaderTitleSize, string> = {
  regular: "1.625rem",
  medium: "1.4rem",
  small: "1.2rem",
};

/* Page title, centred between the two side slots: left/right insets come from
   the wider slot (titleInset), so it never runs into a button. */
const HeaderTitle = styled.h1<{ $size: HeaderTitleSize }>`
  position: absolute;
  top: var(--app-header-center, 40%);
  transform: translateY(-50%);
  margin: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${({ $size }) => HEADER_TITLE_SIZE[$size]};
  font-weight: var(--app-font-heading-weight, 700);
  letter-spacing: -0.01em;

  /* Same desktop step as the old ScreenTitle: the band is wide enough there. */
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

/* Plain glyph action (burger, island settings). Padding doubles as tap target
   and edge inset; ink is stated because a <button> does NOT inherit color from
   the toolbar (UA buttontext/black) — see the Icon atom's color: inherit. */
const HeaderIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: var(--app-color-primary-contrast, #041723);
`;

/* Round tel: action — an anchor, so the OS dialer handling is native. Rides the
   secondary (action) family like the back pill: it must stand out from the
   primary chrome, and on Mental+ it is a crisis line (3114). */
const HeaderCallButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background-color: var(--app-color-secondary, #fecf48);
  color: var(--app-color-secondary-contrast, #041723);
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1);
  text-decoration: none;
`;

const HeaderBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  height: 46px;
  padding: 0 6px 0 2px;
  /* Sits ON the primary-colored wave header, so it takes the secondary
     (action) color to stand out from the chrome instead of blending in. */
  background-color: var(--app-color-secondary, #fecf48);
  color: var(--app-color-secondary-contrast, #041723);
  border: none;
  border-radius: 0 24px 24px 0;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;

  @media (min-width: 800px) {
    border-radius: 24px;
    margin-left: 12px;
  }
`;

const BigLogoWrapper = styled.div`
  position: absolute;
  top: calc(25vh * (163 / 500)); /* Bottom of the toolbar */
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center; /* Center the logo horizontally */
  transform: translateY(-50%); /* Center vertically on toolbar bottom */
  z-index: 10;
  pointer-events: none;
`;


const BigLogo = styled.img`
  max-width: 18%;
  max-height: 16vh;

  @media only screen and (max-width: 480px) {
    max-height: 18vh;
    max-width: 30%;
  }
`;


const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: inherit;
  font-size: 28px;
  line-height: 1;
  padding: 8px 12px;
  cursor: pointer;
`;

const WaveToolbar = styled(IonToolbar)<{ $headerBg?: string }>`
  padding: 0;
  position: relative;
  display: flex;
  height: calc(38vh * (163 / 500));

  /* Ionic internals */
  --opacity: 0;
  --background: transparent;

  /* What you had inline */
  background-color: ${({ $headerBg }) => $headerBg};

  /* Title and slot buttons share one center line: inside the solid part of a
     wave / clouds band, the middle of a flat / round one. */
  --app-header-center: 40%;
  html[data-header-shape="flat"] &,
  html[data-header-shape="round"] & {
    --app-header-center: 50%;
  }

  /* Wave band: the SVG is single-color art, so paint the primary (chrome)
     token through its alpha with a mask. A ::before layer (not a mask on the
     toolbar itself) keeps $headerBg visible beneath the wave's transparent
     bottom; slotted content lives in the shadow .toolbar-container
     (z-index: 10) so it still paints above this layer. */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-color: var(--app-color-primary, #abf0ef);
    -webkit-mask-image: url("/assets/images/header-background-main.svg");
    mask-image: url("/assets/images/header-background-main.svg");
    -webkit-mask-position: left top;
    mask-position: left top;
    -webkit-mask-repeat: repeat-x;
    mask-repeat: repeat-x;
    -webkit-mask-size: auto 100%;
    mask-size: auto 100%;
  }

  /* iOS Safari seam fix */
  @supports (-webkit-touch-callout: none) {
    &::before {
      -webkit-mask-size: auto 101.3%;
      mask-size: auto 101.3%;
    }
  }

  /* Researcher header-shape option (THEME shape.header -> data attribute set
     in tokens.ts): 'flat' drops the wave mask for a solid band; 'round'
     additionally softens the band with a bottom arc; 'clouds' swaps the wave
     for a scalloped cloud edge (same tiling as the wave). Default/absent = wave. */
  html[data-header-shape="flat"] &::before,
  html[data-header-shape="round"] &::before {
    -webkit-mask-image: none;
    mask-image: none;
  }
  html[data-header-shape="round"] &::before {
    border-radius: 0 0 26px 26px;
  }
  html[data-header-shape="clouds"] &::before {
    -webkit-mask-image: url("/assets/images/header-background-clouds.svg");
    mask-image: url("/assets/images/header-background-clouds.svg");
  }

  /* The page title (ScreenTitle) rides the primary-colored wave here, so it
     takes the primary contrast ink instead of its default text ink. */
  ion-title h1 {
    color: var(--app-color-primary-contrast, #393939);
  }
`;

export default ResonanceLayout;